import { Component, effect, inject } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { signOut } from '@aws-amplify/auth';
import { CommonModule } from '@angular/common';
import { LogoComponent } from '../../icons/logo';
import { HomeComponent } from '../../icons/home';
import { LocaleService } from 'src/app/services/locale';
import { UsersComponent } from '../../icons/users';
import { UserComponent } from '../../icons/user';
import { ItComponent } from '../../icons/it';
import { EnComponent } from '../../icons/en';
import { LogoutComponent } from '../../icons/logout';
import { SidebarComponent } from './sidebar';
import { CreditComponent } from '../../icons/video';
import { UserService } from 'src/app/services/user';
import { CompanyService } from 'src/app/services/company';

@Component({
  selector: 'app-dashboard',
  imports: [
    RouterOutlet,
    RouterModule,
    CommonModule,
    LogoComponent,
    HomeComponent,
    UsersComponent,
    UserComponent,
    ItComponent,
    EnComponent,
    LogoutComponent,
    SidebarComponent,
    CreditComponent
  ],
  template: `
    <!-- Mobile Menu Button -->
    <button
      (click)="isMenuOpen = !isMenuOpen"
      class="fixed top-4 right-4 z-[60] lg:hidden p-2 rounded-lg bg-white shadow-md"
    >
      <svg *ngIf="!isMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
      </svg>
      <svg *ngIf="isMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    </button>

    <!-- Header -->
    <div
      class="flex items-center py-3 px-4 lg:pr-12 w-full fixed top-0 backdrop-blur-3xl z-50 bg-white/80 border-b border-neutral-200"
    >
      <a routerLink="/dashboard/calculations" class="flex-shrink-0">
        <div class="cursor-pointer">
          <app-logo></app-logo>
        </div>
      </a>

      <!-- Desktop Navigation -->
      <div class="hidden lg:flex ml-auto xl:ml-[320px] gap-1 xl:gap-2 futura items-center">
        <a
          routerLink="/dashboard/calculations"
          routerLinkActive="text-white bg-[#043B22]"
          class="flex gap-2 px-3 xl:px-4 py-2 items-center cursor-pointer text-nowrap transition-all duration-200 rounded-lg"
          [ngClass]="{'hover:bg-gray-100': !rla1.isActive, 'hover:bg-[#032d1a]': rla1.isActive}"
          #rla1="routerLinkActive"
        >
          <app-home [color]="rla1.isActive ? 'white' : 'black'"></app-home>
          <p class="mt-1 text-xs xl:text-sm">
            {{ locale.dashboard.header_carbon_footprint }}
          </p>
        </a>
        <a
          routerLink="/dashboard/emission"
          routerLinkActive="text-white bg-[#043B22]"
          class="flex gap-2 px-3 xl:px-4 py-2 items-center cursor-pointer transition-all duration-200 rounded-lg"
          [ngClass]="{'hover:bg-gray-100': !rla2.isActive, 'hover:bg-[#032d1a]': rla2.isActive}"
          #rla2="routerLinkActive"
        >
          <app-users [color]="rla2.isActive ? 'white' : 'black'"></app-users>
          <p class="mt-1 text-xs xl:text-sm">
            {{ locale.dashboard.header_emissions }}
          </p>
        </a>
        <a
          routerLink="/dashboard/training"
          routerLinkActive="text-white bg-[#043B22]"
          class="flex gap-2 px-3 xl:px-4 py-2 items-center cursor-pointer transition-all duration-200 rounded-lg"
          [ngClass]="{'hover:bg-gray-100': !rla3.isActive, 'hover:bg-[#032d1a]': rla3.isActive}"
          #rla3="routerLinkActive"
        >
          <app-credit [color]="rla3.isActive ? 'white' : 'black'"></app-credit>
          <p class="mt-1 text-xs xl:text-sm">
            {{ locale.dashboard.training }}
          </p>
        </a>
      </div>

      <!-- Right Actions -->
      <div class="hidden lg:flex ml-auto items-center gap-1">
        <div
          *ngIf="language === 'italian'"
          (click)="localeService.switchLocale()"
          class="p-2 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
        >
          <app-it></app-it>
        </div>
        <div
          *ngIf="language === 'english'"
          (click)="localeService.switchLocale()"
          class="p-2 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
        >
          <app-en></app-en>
        </div>
        <a
          routerLink="/dashboard/profile"
          class="p-2 cursor-pointer rounded-lg transition-colors"
          routerLinkActive="text-white bg-[#043B22]"
          #rla4="routerLinkActive"
        >
          <app-user [color]="rla4.isActive ? 'white' : 'black'"></app-user>
        </a>
        <div
          *ngIf="rla4.isActive"
          (click)="confirm = true"
          class="p-2 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
        >
          <app-logout></app-logout>
        </div>
      </div>
    </div>

    <!-- Mobile Sidebar Overlay -->
    <div
      *ngIf="isMenuOpen"
      (click)="isMenuOpen = false"
      class="fixed inset-0 bg-black/50 z-40 lg:hidden"
    ></div>

    <!-- Main Content Area -->
    <div class="flex w-full mt-22 sm:mt-24 lg:mt-26 overflow-x-hidden">
      <!-- Sidebar -->
      <div
        [ngClass]="{'translate-x-0': isMenuOpen, '-translate-x-full lg:translate-x-0': !isMenuOpen}"
        class="fixed bg-white left-0 top-22 sm:top-24 lg:top-26 z-40 lg:z-20 h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] lg:h-[calc(100vh-4.5rem)] w-[280px] sm:w-[320px] xl:w-[380px] border-r border-neutral-200"
      >
        <!-- Mobile Navigation in Sidebar -->
        <div class="lg:hidden px-4 sm:px-6 mb-6 pb-6 border-b border-gray-200">
          <div class="flex flex-col gap-2 futura">
            <a
              routerLink="/dashboard/calculations"
              (click)="isMenuOpen = false"
              routerLinkActive="text-white bg-[#043B22]"
              class="flex gap-3 px-4 py-3 items-center cursor-pointer rounded-lg transition-colors text-xs sm:text-sm"
              #rla1m="routerLinkActive"
            >
              <app-home [color]="rla1m.isActive ? 'white' : 'black'"></app-home>
              <p>{{ locale.dashboard.header_carbon_footprint }}</p>
            </a>
            <a
              routerLink="/dashboard/emission"
              (click)="isMenuOpen = false"
              routerLinkActive="text-white bg-[#043B22]"
              class="flex gap-3 px-4 py-3 items-center cursor-pointer rounded-lg transition-colors text-xs sm:text-sm"
              #rla2m="routerLinkActive"
            >
              <app-users [color]="rla2m.isActive ? 'white' : 'black'"></app-users>
              <p>{{ locale.dashboard.header_emissions }}</p>
            </a>
            <a
              routerLink="/dashboard/training"
              (click)="isMenuOpen = false"
              routerLinkActive="text-white bg-[#043B22]"
              class="flex gap-3 px-4 py-3 items-center cursor-pointer rounded-lg transition-colors text-xs sm:text-sm"
              #rla3m="routerLinkActive"
            >
              <app-credit [color]="rla3m.isActive ? 'white' : 'black'"></app-credit>
              <p>{{ locale.dashboard.training }}</p>
            </a>
            <a
              routerLink="/dashboard/profile"
              (click)="isMenuOpen = false"
              routerLinkActive="text-white bg-[#043B22]"
              class="flex gap-3 px-4 py-3 items-center cursor-pointer rounded-lg transition-colors text-xs sm:text-sm"
              #rla4m="routerLinkActive"
            >
              <app-user [color]="rla4m.isActive ? 'white' : 'black'"></app-user>
              <p>{{ locale.dashboard.header_profile || 'Profilo' }}</p>
            </a>
          </div>
          <div class="flex items-center gap-2 mt-4 px-4">
            <div
              *ngIf="language === 'italian'"
              (click)="localeService.switchLocale()"
              class="p-2 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
            >
              <app-it></app-it>
            </div>
            <div
              *ngIf="language === 'english'"
              (click)="localeService.switchLocale()"
              class="p-2 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
            >
              <app-en></app-en>
            </div>
            <div
              (click)="confirm = true"
              class="p-2 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors ml-auto"
            >
              <app-logout></app-logout>
            </div>
          </div>
        </div>
        <app-sidebar (companySelected)="isMenuOpen = false"></app-sidebar>
      </div>

      <!-- Main Content -->
      <div class="w-full lg:ml-[320px] xl:ml-[380px] px-4 sm:px-6 lg:px-8 xl:pr-10 pb-8">
        <router-outlet></router-outlet>
      </div>
    </div>

    <!-- Logout Confirmation Modal -->
    <div
      *ngIf="confirm"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4"
      (click)="confirm = false"
    >
      <div
        class="bg-white rounded-2xl w-full max-w-[340px] shadow-2xl overflow-hidden"
        (click)="$event.stopPropagation()"
      >
        <!-- Icon Section -->
        <div class="pt-6 pb-4 flex justify-center">
          <div class="w-16 h-16 bg-[#043B22]/10 rounded-full flex items-center justify-center">
            <div class="w-12 h-12 bg-gradient-to-br from-[#043B22] to-[#065a35] rounded-full flex items-center justify-center shadow-lg shadow-[#043B22]/20">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="px-6 pb-2 text-center">
          <h3 class="futura text-[15px] text-[#043B22] mb-2">{{ locale.close.heading }}</h3>
          <p class="text-[11px] text-[#616161] leading-relaxed">
            {{ locale.close.logout_message || 'Sei sicuro di voler uscire dal tuo account?' }}
          </p>
        </div>

        <!-- Actions -->
        <div class="p-5 pt-4 flex gap-3">
          <button
            (click)="confirm = false"
            class="flex-1 px-4 py-2.5 text-[11px] font-semibold text-[#616161] bg-[#f3f4f6] hover:bg-[#e5e7eb] border border-[#e5e7eb] rounded-xl transition-all cursor-pointer"
          >
            {{ locale.close.no }}
          </button>
          <button
            (click)="logout()"
            class="flex-1 px-4 py-2.5 text-[11px] font-semibold text-white bg-gradient-to-r from-[#043B22] to-[#065a35] hover:from-[#065a35] hover:to-[#043B22] rounded-xl transition-all cursor-pointer shadow-md shadow-[#043B22]/30"
          >
            {{ locale.close.yes }}
          </button>
        </div>
      </div>
    </div>
  `
})
export class DashboardComponent {
  private router: Router = inject(Router);
  private spinner = inject(NgxSpinnerService);
  private userService = inject(UserService);
  private companyService = inject(CompanyService);

  localeService = inject(LocaleService);
  locale = this.localeService.locale();
  language = this.localeService.getLanguage();
  isMenuOpen = false;

  constructor() {
    effect(() => {
      this.locale = this.localeService.locale();
      this.language = this.localeService.getLanguage();
    });
  }

  confirm = false;

  logout = async () => {
    this.spinner.show();
    this.userService.user.set(null);
    this.companyService.company.set(null);
    this.companyService.companies.set([]);
    await signOut();
    localStorage.clear();
    this.spinner.hide();
    this.router.navigateByUrl('/login?lo=y');
  };
}
